import React, { useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight, Sparkles } from 'lucide-react';
import { Reveal } from './Reveal';

const CourseSection = () => {
  const { t, language } = useLanguage();
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);

  const benefits = language === 'hu'
    ? ['Step-by-step videók kezdőknek', 'Valós példák, esettanulmányok', 'AI eszközök használata', 'Közösségi média stratégiák', 'Affiliate marketing alapok']
    : ['Step-by-step videos for beginners', 'Real examples, case studies', 'Using AI tools', 'Social media strategies', 'Affiliate marketing basics'];

  return (
    <section
      id="course"
      ref={sectionRef}
      className="relative py-28 md:py-40 px-6 md:px-12 bg-surface overflow-hidden"
      data-testid="course-section"
    >
      <div className="max-w-[1480px] mx-auto">
        {/* Section heading */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16 md:mb-24">
          <div className="lg:col-span-5">
            <Reveal as="p" className="font-body text-[0.72rem] uppercase tracking-[0.32em] text-accent mb-6">
              {language === 'hu' ? '02 — Az Online Kurzus' : '02 — The Online Course'}
            </Reveal>
            <Reveal as="h2" duration={1.1} className="font-heading text-5xl md:text-6xl lg:text-7xl text-text-primary leading-[0.95] tracking-tight" data-testid="course-title">
              <span className="block font-light">{t.course.title.split(' ')[0]}</span>
              <span className="block italic font-extralight text-text-secondary">
                {t.course.title.split(' ').slice(1).join(' ') || t.course.title}
              </span>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <Reveal as="p" delay={0.15} className="font-heading text-2xl md:text-3xl italic text-text-secondary leading-snug max-w-md">
              {t.course.subtitle}
            </Reveal>
          </div>
        </div>

        {/* Editorial split */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-0 items-stretch">
          {/* Image side */}
          <div className="lg:col-span-7 relative">
            <motion.div
              style={{ y: imgY }}
              className="relative aspect-[4/5] md:aspect-[5/6] rounded-3xl overflow-hidden grain-layer shadow-editorial"
            >
              <img
                src="/img/course.webp"
                alt="Online Course"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                data-testid="course-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-text-primary/20 via-transparent to-transparent" />
            </motion.div>

            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, delay: 0.4, ease: [0.16,1,0.3,1] }}
              className="hidden md:flex absolute top-8 left-8 items-center gap-3 bg-background/85 backdrop-blur-md border border-border/70 px-5 py-3 rounded-full"
            >
              <Sparkles size={14} className="text-accent" strokeWidth={1.5} />
              <span className="font-body text-[0.65rem] uppercase tracking-[0.3em] text-text-primary">
                {language === 'hu' ? 'Bővített Kiadás · 2026' : 'Extended Edition · 2026'}
              </span>
            </motion.div>
          </div>

          {/* Text card — overlapping */}
          <div className="lg:col-span-6 lg:col-start-7 relative lg:-mt-12 lg:-ml-10 z-10">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1.2, ease: [0.16,1,0.3,1] }}
              className="relative bg-background border border-border/70 rounded-3xl p-8 md:p-12 lg:p-14 shadow-editorial"
            >
              <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed mb-10">
                {t.course.description}
              </p>

              {/* Two blocks: who / problem */}
              <div className="space-y-8 mb-10">
                <div>
                  <p className="font-body text-[0.65rem] uppercase tracking-[0.3em] text-accent mb-3">
                    {t.course.whoIsItFor}
                  </p>
                  <p className="font-heading text-xl md:text-2xl italic text-text-primary leading-snug">
                    {t.course.whoIsItForText}
                  </p>
                </div>
                <div className="editorial-divider" />
                <div>
                  <p className="font-body text-[0.65rem] uppercase tracking-[0.3em] text-accent mb-3">
                    {t.course.whatProblem}
                  </p>
                  <p className="font-heading text-xl md:text-2xl italic text-text-primary leading-snug">
                    {t.course.whatProblemText}
                  </p>
                </div>
              </div>

              {/* Benefits */}
              <ul className="space-y-3 mb-12">
                {benefits.map((b, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * i, duration: 0.8, ease: [0.16,1,0.3,1] }}
                    className="flex items-center gap-4 group"
                  >
                    <span className="w-6 h-px bg-accent group-hover:w-10 transition-all duration-700" />
                    <span className="font-body text-sm md:text-base text-text-primary/85 tracking-wide">{b}</span>
                  </motion.li>
                ))}
              </ul>

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-6">
                <a
                  href="https://www.penzkereses.online/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-magnetic group inline-flex items-center gap-4 bg-text-primary text-background pl-7 pr-3 py-3 rounded-full hover:bg-accent transition-colors duration-700"
                  data-testid="course-cta-primary"
                >
                  <span className="font-body text-[0.78rem] uppercase tracking-[0.25em]">{t.course.cta}</span>
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-background text-text-primary group-hover:rotate-45 transition-transform duration-700">
                    <ArrowUpRight size={16} strokeWidth={1.5} />
                  </span>
                </a>
                <a
                  href="https://www.penzkereses.online/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline font-body text-[0.78rem] uppercase tracking-[0.25em] text-text-primary"
                  data-testid="course-cta-secondary"
                >
                  {t.course.learnMore}
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CourseSection;
